import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, MessageCircle, UserX, MoreVertical, Ban, Flag } from 'lucide-react';
import AppShell from '../components/AppShell';
import { API, authHeaders, fmtErr, mediaUrl } from '../lib/auth';
import { useFlash } from '../lib/useMe';

export default function MemberProfilePage() {
  const { kryshaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = useFlash();
  const [profile, setProfile] = useState(location.state?.profile || null);
  const [loading, setLoading] = useState(!location.state?.profile);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (location.state?.profile) return;
    setLoading(true);
    axios.get(`${API}/users/search/${kryshaId}`, { headers: authHeaders() })
      .then(({ data }) => setProfile(data))
      .catch((err) => flash(fmtErr(err.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kryshaId]);

  const back = () => navigate(-1);

  const guarded = async (msg, fn) => {
    if (!window.confirm(msg)) return;
    try {
      const { data } = await fn();
      flash(data.message);
      back();
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const unmatch = () => guarded(`Unmatch with ${profile.first_name}? This removes them from your Inbox.`,
    () => axios.post(`${API}/connections/${profile.connection_id}/unmatch`, {}, { headers: authHeaders() }));
  const block = () => guarded(`Block ${profile.first_name}? They won't be able to contact you again.`,
    () => axios.post(`${API}/users/block`, { krysha_id: profile.krysha_id }, { headers: authHeaders() }));
  const reportAndBlock = () => guarded(`Block and report ${profile.first_name} as spam?`,
    () => axios.post(`${API}/users/report`, { krysha_id: profile.krysha_id }, { headers: authHeaders() }));

  const message = () => navigate(profile.conversation_id ? `/inbox?conv=${profile.conversation_id}` : '/inbox');

  if (loading) return <AppShell title="Profile"><p style={{ color: 'var(--muted)' }}>Loading profile…</p></AppShell>;
  if (!profile) {
    return (
      <AppShell title="Profile" testid="member-profile-page">
        <button className="btn btn-ghost btn-sm" onClick={back} data-testid="member-profile-back-btn"><ArrowLeft size={14} /> Back</button>
        <p style={{ color: 'var(--muted)', marginTop: 14 }}>This profile isn't available.</p>
      </AppShell>
    );
  }

  const photos = profile.photos?.length > 0 ? profile.photos : (profile.photo_url ? [profile.photo_url] : []);

  return (
    <AppShell testid="member-profile-page">
      <div className="dash-card profile-view" data-testid="member-profile-card">
        <div className="pv-top">
          <button className="btn btn-ghost btn-sm" onClick={back} data-testid="member-profile-back-btn"><ArrowLeft size={14} /> Back</button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-red btn-sm" onClick={message} data-testid="member-profile-message-btn"><MessageCircle size={14} /> Message</button>
            {profile.connection_id && (
              <button className="btn btn-ghost btn-sm" onClick={unmatch} data-testid="member-profile-unmatch-btn"><UserX size={14} /> Unmatch</button>
            )}
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setMenuOpen((o) => !o)} data-testid="member-profile-more-btn"><MoreVertical size={14} /></button>
              {menuOpen && (
                <div className="kebab-menu" data-testid="member-profile-more-menu">
                  <button onClick={block} data-testid="member-profile-block-btn"><Ban size={14} /> Block</button>
                  <button onClick={reportAndBlock} data-testid="member-profile-report-btn"><Flag size={14} /> Block &amp; Report Spam</button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="pv-gallery" data-testid="member-profile-gallery">
          {photos.length > 0 ? photos.map((u, i) => (
            <img key={i} className={`pv-photo ${i === 0 ? 'pv-main' : ''}`} src={mediaUrl(u, true)} alt={`${profile.first_name} photo ${i + 1}`} data-testid={`member-profile-photo-${i}`} />
          )) : <div className="pv-photo pv-main pv-empty">{profile.first_name?.[0] || '?'}</div>}
        </div>
        <div className="pv-info">
          <h2 data-testid="member-profile-name">{profile.first_name}{profile.age ? `, ${profile.age}` : ''}</h2>
          <div className="pv-meta">
            {[profile.city, profile.country].filter(Boolean).join(', ') || 'Location not shared'} · <span className="pv-kid">{profile.krysha_id}</span>
          </div>
          {profile.headline && <p className="pv-headline" data-testid="member-profile-headline">“{profile.headline}”</p>}
          {profile.about && <p className="pv-about" data-testid="member-profile-about">{profile.about}</p>}
          <div className="pv-facts">
            {profile.personality && <div className="pv-fact"><b>Personality</b><span>{profile.personality}</span></div>}
            {profile.looking && <div className="pv-fact"><b>Looking For</b><span>{profile.looking}</span></div>}
          </div>
          {profile.interests?.length > 0 && (
            <div className="pv-section"><h4>Interests</h4><div className="ttags">{profile.interests.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
          )}
          {profile.lifestyle?.length > 0 && (
            <div className="pv-section"><h4>Lifestyle</h4><div className="ttags">{profile.lifestyle.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
          )}
          <p className="dash-privacy" style={{ marginTop: 14 }}>Email, mobile and WhatsApp numbers are always kept private.</p>
        </div>
      </div>
    </AppShell>
  );
}
